/**
 * Black-Scholes European-option pricing.
 *
 * Vanilla equity options are American and can be exercised early, so BS is
 * technically an approximation here. For calls on a non-dividend-paying
 * stock it is exact; for puts and dividend-payers it is a good enough
 * display tool for the "what if the underlying is at X in Y days" chart.
 * Nothing in this file touches order flow, only the UI's theoretical price.
 */

/** Cumulative standard normal distribution via Abramowitz & Stegun 26.2.17.
 *  Max error ~7.5e-8 in [0, ∞), which is well below display precision. */
export function cdfNormal(x: number): number {
  if (!Number.isFinite(x)) return x > 0 ? 1 : 0;
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * absX);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-absX * absX);
  return 0.5 * (1 + sign * y);
}

export interface BsInput {
  spot: number;
  strike: number;
  /** Years to expiration — not days. Pass `days / 365`. */
  tYears: number;
  /** Implied volatility as a decimal (e.g. 0.4 for 40%). */
  sigma: number;
  /** Annual risk-free rate as a decimal. Default 4%. */
  r?: number;
  /** Annual continuous dividend yield as a decimal. Default 0. */
  q?: number;
  kind: "call" | "put";
}

/** Fair value of a European call or put under Black-Scholes-Merton.
 *  Degenerate inputs collapse to the intrinsic payoff, so a user who
 *  scrubs the time slider all the way to expiration sees the kink line
 *  identically to the expiration-only mode. */
export function bsPrice({
  spot: S, strike: K, tYears: T, sigma, r = 0.04, q = 0, kind,
}: BsInput): number {
  if (!(S > 0) || !(K > 0) || !Number.isFinite(T) || T <= 0 || !(sigma > 0)) {
    const intrinsic = kind === "call"
      ? Math.max(S - K, 0)
      : Math.max(K - S, 0);
    return intrinsic;
  }
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r - q + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const dfR = Math.exp(-r * T);
  const dfQ = Math.exp(-q * T);
  if (kind === "call") {
    return S * dfQ * cdfNormal(d1) - K * dfR * cdfNormal(d2);
  }
  return K * dfR * cdfNormal(-d2) - S * dfQ * cdfNormal(-d1);
}
